package br.com.vvrefeicoes.portal.feature.rmp

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.file.isPdfUploadSupported
import br.com.vvrefeicoes.portal.domain.model.RmpFieldKind
import br.com.vvrefeicoes.portal.domain.model.RmpFormField
import br.com.vvrefeicoes.portal.domain.model.RmpFormSchema
import com.composables.icons.lucide.ArrowLeft
import com.composables.icons.lucide.Building2
import com.composables.icons.lucide.Download
import com.composables.icons.lucide.FileText
import com.composables.icons.lucide.List
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Save
import com.composables.icons.lucide.Send
import com.composables.icons.lucide.Trash2
import com.composables.icons.lucide.Upload
import com.composables.icons.lucide.X
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rmp_edit_save
import portalvv.shared.generated.resources.rmp_edit_subtitle
import portalvv.shared.generated.resources.rmp_edit_title
import portalvv.shared.generated.resources.rmp_new_attach
import portalvv.shared.generated.resources.rmp_new_attach_pending
import portalvv.shared.generated.resources.rmp_new_attach_unsupported
import portalvv.shared.generated.resources.rmp_new_back
import portalvv.shared.generated.resources.rmp_new_date_placeholder
import portalvv.shared.generated.resources.rmp_new_month_end
import portalvv.shared.generated.resources.rmp_new_month_start
import portalvv.shared.generated.resources.rmp_new_no_units
import portalvv.shared.generated.resources.rmp_new_required_hint
import portalvv.shared.generated.resources.rmp_new_select_placeholder
import portalvv.shared.generated.resources.rmp_new_submit
import portalvv.shared.generated.resources.rmp_new_subtitle
import portalvv.shared.generated.resources.rmp_new_title
import portalvv.shared.generated.resources.rmp_new_type
import portalvv.shared.generated.resources.rmp_new_type_placeholder
import portalvv.shared.generated.resources.rmp_retry

private val FormMaxWidth = 960.dp

@Composable
fun RmpNewScreen(
    onBack: () -> Unit,
    editId: String? = null,
) {
    val controller = rememberRmpNewController()

    LaunchedEffect(controller) {
        controller.init(editId)
    }

    AppScrollColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(AppSpacing.space24),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        footer = { AppFooter() },
    ) {
        Row(verticalAlignment = Alignment.Top) {
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(AppSpacing.space4),
            ) {
                val number =
                    controller.current
                        ?.number
                        ?.toString()
                        .orEmpty()
                Text(
                    text =
                        if (editId == null) {
                            stringResource(Res.string.rmp_new_title)
                        } else {
                            stringResource(Res.string.rmp_edit_title, number)
                        },
                    style = MaterialTheme.typography.headlineMedium,
                )
                Text(
                    text = stringResource(if (editId == null) Res.string.rmp_new_subtitle else Res.string.rmp_edit_subtitle),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            AppButton(
                text = stringResource(Res.string.rmp_new_back),
                onClick = onBack,
                variant = PortalButtonVariant.Text,
                leadingIcon = Lucide.ArrowLeft,
            )
        }

        controller.feedback?.let { feedback ->
            AppBanner(text = feedback.message, onDismiss = controller::dismissFeedback, isError = feedback.isError)
        }

        val schema = controller.schema
        when {
            controller.loading ->
                Box(modifier = Modifier.fillMaxWidth().height(240.dp), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
            schema == null ->
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                    Text(
                        text = controller.errorMessage.orEmpty(),
                        color = MaterialTheme.colorScheme.error,
                        modifier = Modifier.weight(1f),
                    )
                    AppButton(
                        text = stringResource(Res.string.rmp_retry),
                        onClick = controller::retry,
                        variant = PortalButtonVariant.Outlined,
                    )
                }
            else -> FormCard(controller, schema)
        }
    }
}

@Composable
private fun FormCard(
    controller: RmpNewController,
    schema: RmpFormSchema,
) {
    Surface(
        modifier = Modifier.fillMaxWidth().widthIn(max = FormMaxWidth),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space24),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        ) {
            if (schema.linkedUnits.isEmpty() && !controller.isEditingSubmitted) {
                Text(
                    text = stringResource(Res.string.rmp_new_no_units),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.error,
                )
            }

            FieldLabel(stringResource(Res.string.rmp_new_type), required = false)
            val placeholder = stringResource(Res.string.rmp_new_type_placeholder)
            val types = controller.submittableTypes
            SearchableSelect(
                leadingIcon = Lucide.FileText,
                options = types.map { it.label },
                selected = controller.selectedType?.label ?: placeholder,
                onSelect = { label -> types.firstOrNull { it.label == label }?.let { controller.selectType(it.value) } },
                enabled = controller.current == null && !controller.saving,
                modifier = Modifier.fillMaxWidth(),
            )

            controller.selectedType?.let {
                controller.visibleFields.forEach { field ->
                    FieldInput(field, controller, schema)
                }
                Text(
                    text = stringResource(Res.string.rmp_new_required_hint),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12, Alignment.End),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                if (controller.saving) CircularProgressIndicator(modifier = Modifier.height(24.dp))
                val canSave = controller.selectedType != null && controller.isEditable && !controller.saving
                AppButton(
                    text =
                        stringResource(
                            if (controller.isEditingSubmitted) Res.string.rmp_edit_save else Res.string.rmp_new_submit,
                        ),
                    onClick = controller::submit,
                    enabled = canSave,
                    leadingIcon = if (controller.isEditingSubmitted) Lucide.Save else Lucide.Send,
                )
            }
        }
    }
}

@Composable
private fun FieldInput(
    field: RmpFormField,
    controller: RmpNewController,
    schema: RmpFormSchema,
) {
    val name = field.name
    if (field.kind == RmpFieldKind.SECTION || name == null) {
        Column(modifier = Modifier.padding(top = AppSpacing.space8), verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
            Text(text = field.label, style = MaterialTheme.typography.titleMedium)
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
        }
        return
    }
    val enabled = controller.isEditable && !controller.saving && !controller.isLocked(field)
    val required = isRequired(field, controller.values)
    val value = controller.values[name]
    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        when (field.kind) {
            RmpFieldKind.TEXT, RmpFieldKind.EMAIL, RmpFieldKind.NUMBER, RmpFieldKind.TEXTAREA -> {
                val limit = field.maxLength ?: if (field.kind == RmpFieldKind.TEXTAREA) 5000 else 255
                AppTextField(
                    value = if (field.readOnly) readOnlyValue(field, controller, schema) else value.text(),
                    onValueChange = { input ->
                        val clean = if (field.kind == RmpFieldKind.NUMBER) input.filter(Char::isDigit) else input
                        controller.setValue(name, RmpFieldValue.Text(clean.take(limit)))
                    },
                    label = labelOf(field, required),
                    placeholder = field.placeholder,
                    singleLine = field.kind != RmpFieldKind.TEXTAREA,
                    enabled = enabled && !field.readOnly,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
            RmpFieldKind.DATE ->
                AppTextField(
                    value = value.text(),
                    onValueChange = { input -> controller.setValue(name, RmpFieldValue.Text(maskDate(input))) },
                    label = labelOf(field, required),
                    placeholder = stringResource(Res.string.rmp_new_date_placeholder),
                    enabled = enabled,
                    modifier = Modifier.fillMaxWidth(),
                )
            RmpFieldKind.CURRENCY ->
                AppTextField(
                    value = value.text(),
                    onValueChange = { input -> controller.setValue(name, RmpFieldValue.Text(maskCurrency(input))) },
                    label = labelOf(field, required),
                    placeholder = "0,00",
                    leadingIcon = { Text("R$") },
                    enabled = enabled,
                    modifier = Modifier.fillMaxWidth(),
                )
            RmpFieldKind.SELECT -> {
                FieldLabel(field.label, required)
                ChoiceSelect(field, schema, value.text(), enabled) { selected ->
                    controller.setValue(name, RmpFieldValue.Text(selected))
                }
            }
            RmpFieldKind.RADIO -> {
                FieldLabel(field.label, required)
                OptionRow(field.options.map { it.value to it.label }) { option, label ->
                    val selected = value.text() == option
                    Row(
                        modifier = Modifier.clickable(enabled = enabled) { controller.setValue(name, RmpFieldValue.Text(option)) },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        RadioButton(
                            selected = selected,
                            onClick = { controller.setValue(name, RmpFieldValue.Text(option)) },
                            enabled = enabled,
                        )
                        Text(label, style = MaterialTheme.typography.bodyMedium)
                    }
                }
            }
            RmpFieldKind.CHECKBOX -> {
                FieldLabel(field.label, required)
                val selected = (value as? RmpFieldValue.Choices)?.values.orEmpty()
                OptionRow(field.options.map { it.value to it.label }) { option, label ->
                    Row(
                        modifier = Modifier.clickable(enabled = enabled) { controller.toggleChoice(name, option) },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Checkbox(checked = option in selected, onCheckedChange = { controller.toggleChoice(name, option) }, enabled = enabled)
                        Text(label, style = MaterialTheme.typography.bodyMedium)
                    }
                }
            }
            RmpFieldKind.MONTH_PERIOD -> {
                FieldLabel(field.label, required)
                val period = value as? RmpFieldValue.Period ?: RmpFieldValue.Period(null, null)
                Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                    MonthSelect(stringResource(Res.string.rmp_new_month_start), period.start, enabled, Modifier.weight(1f)) {
                        controller.setValue(name, period.copy(start = it))
                    }
                    MonthSelect(stringResource(Res.string.rmp_new_month_end), period.end, enabled, Modifier.weight(1f)) {
                        controller.setValue(name, period.copy(end = it))
                    }
                }
            }
            RmpFieldKind.FILE, RmpFieldKind.MULTI_FILE -> {
                FieldLabel(field.label, required)
                FileField(field, controller, enabled)
            }
            RmpFieldKind.SECTION, RmpFieldKind.UNKNOWN -> Unit
        }
        field.note?.let { note ->
            Text(note, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
    }
}

@Composable
private fun ChoiceSelect(
    field: RmpFormField,
    schema: RmpFormSchema,
    selectedValue: String,
    enabled: Boolean,
    onSelect: (String) -> Unit,
) {
    val placeholder = stringResource(Res.string.rmp_new_select_placeholder)
    val options = remember(field, schema) { optionsFor(field, schema) }
    SearchableSelect(
        leadingIcon = if (field.source != null) Lucide.Building2 else Lucide.List,
        options = listOf(placeholder) + options.map { it.second },
        selected = options.firstOrNull { it.first == selectedValue }?.second ?: placeholder,
        onSelect = { label -> onSelect(options.firstOrNull { it.second == label }?.first.orEmpty()) },
        enabled = enabled,
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun MonthSelect(
    label: String,
    month: Int?,
    enabled: Boolean,
    modifier: Modifier,
    onSelect: (Int?) -> Unit,
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        Text(label, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        val placeholder = stringResource(Res.string.rmp_new_select_placeholder)
        SearchableSelect(
            leadingIcon = Lucide.List,
            options = listOf(placeholder) + RmpMonths,
            selected = month?.let { RmpMonths.getOrNull(it) } ?: placeholder,
            onSelect = { selected -> onSelect(RmpMonths.indexOf(selected).takeIf { it >= 0 }) },
            enabled = enabled,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

@Composable
private fun FileField(
    field: RmpFormField,
    controller: RmpNewController,
    enabled: Boolean,
) {
    val name = field.name ?: return
    val pendingLabel = stringResource(Res.string.rmp_new_attach_pending)
    controller.attachments.filter { it.field == name }.forEach { attachment ->
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
            Text(attachment.fileName, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
            IconButton(onClick = { controller.downloadAttachment(attachment) }) {
                Icon(Lucide.Download, contentDescription = null)
            }
            if (enabled) {
                IconButton(onClick = { controller.removeAttachment(attachment) }) {
                    Icon(Lucide.Trash2, contentDescription = null)
                }
            }
        }
    }
    controller.pendingFiles[name].orEmpty().forEachIndexed { index, file ->
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
            Text("${file.name} ($pendingLabel)", style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
            IconButton(onClick = { controller.removePendingFile(name, index) }) {
                Icon(Lucide.X, contentDescription = null)
            }
        }
    }
    if (isPdfUploadSupported()) {
        AppButton(
            text = stringResource(Res.string.rmp_new_attach),
            onClick = { controller.pickFile(field) },
            enabled = enabled,
            variant = PortalButtonVariant.Outlined,
            leadingIcon = Lucide.Upload,
        )
    } else {
        Text(
            stringResource(Res.string.rmp_new_attach_unsupported),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun OptionRow(
    options: List<Pair<String, String>>,
    item: @Composable (String, String) -> Unit,
) {
    FlowRow(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
        options.forEach { (value, label) -> item(value, label) }
    }
}

@Composable
private fun FieldLabel(
    text: String,
    required: Boolean,
) {
    Text(
        text = if (required) "$text *" else text,
        style = MaterialTheme.typography.labelLarge,
        color = MaterialTheme.colorScheme.onSurface,
    )
}

private fun labelOf(
    field: RmpFormField,
    required: Boolean,
): String = if (required) "${field.label} *" else field.label

/** Campo calculado (centro de custo da admissão): o escolhido ou, na falta, a unidade. */
private fun readOnlyValue(
    field: RmpFormField,
    controller: RmpNewController,
    schema: RmpFormSchema,
): String {
    if (field.name != "cc") return controller.values[field.name.orEmpty()].text()
    val unitId = controller.values[RMP_COST_CENTER_FIELD].text().ifBlank { controller.values[RMP_UNIT_FIELD].text() }
    return schema.allUnits.firstOrNull { it.id == unitId }?.name ?: controller.values["cc"].text()
}
